"use client";

import type { Variants } from "framer-motion";
import { motion, useAnimation } from "framer-motion";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

import { cn } from "@/lib/utils";

export interface AwardIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface AwardIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  color?: string;
}

const CIRCLE_VARIANTS: Variants = {
  initial: {
    scale: 1,
    strokeWidth: 2,
    fill: "none",
  },
  hover: {
    scale: [1, 1.1, 1],
    strokeWidth: 2.5,
    fill: "#B8883D",
    transition: {
      duration: 0.5,
      ease: "easeInOut",
    },
  },
};

const RIBBON_VARIANTS: Variants = {
  initial: {
    y: 0,
    rotate: 0,
  },
  hover: {
    y: [0, 1, 0],
    rotate: [0, 5, -5, 0],
    transition: {
      duration: 1.2,
      ease: "easeInOut",
    },
  },
};

const AwardIcon = forwardRef<AwardIconHandle, AwardIconProps>(
  ({ onMouseEnter, onMouseLeave, className, size = 28, color = "currentColor", ...props }, ref) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => {
          controls.start("hover");
        },
        stopAnimation: () => {
          controls.start("initial");
        },
      };
    });

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseEnter?.(e);
        } else {
          controls.start("hover");
        }
      },
      [onMouseEnter, controls]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseLeave?.(e);
        } else {
          controls.start("initial");
        }
      },
      [controls, onMouseLeave]
    );

    return (
      <div
        className={cn(className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Ribbons */}
          <motion.path
            animate={controls}
            d="m8.21 13.89L7 23l5-3 5 3-1.21-9.12"
            variants={RIBBON_VARIANTS}
          />
          {/* Main Medal Circle */}
          <motion.circle
            animate={controls}
            cx="12"
            cy="8"
            r="7"
            variants={CIRCLE_VARIANTS}
          />
        </svg>
      </div>
    );
  }
);

AwardIcon.displayName = "AwardIcon";

export { AwardIcon };
